import React, { useState, useEffect, useCallback } from "react";
import { courseAPI } from "../services/api";
import TeksTab from "../components/curriculum/TeksTab";
import UnitsLessonsTab from "../components/curriculum/UnitsLessonsTab";
import ScopeSequenceTab from "../components/curriculum/ScopeSequenceTab";
import UnitGuideTab from "../components/curriculum/UnitGuideTab";
import PedagogyTab from "../components/curriculum/PedagogyTab";
import AssetsTab from "../components/curriculum/AssetsTab";
import "./CurriculumPage.css";

const TABS = [
  { id: "teks", label: "TEKS" },
  { id: "units-lessons", label: "Units & Lessons" },
  { id: "scope-sequence", label: "Scope & Sequence" },
  { id: "unit-guides", label: "Unit Guides" },
  { id: "pedagogy", label: "Pedagogy" },
  { id: "assets", label: "Assets" },
];

function CurriculumPage({ authProfile }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [activeTab, setActiveTab] = useState("teks");
  const [courseKpis, setCourseKpis] = useState(null);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [coursesError, setCoursesError] = useState("");

  const loadCourses = useCallback(async () => {
    setLoadingCourses(true);
    setCoursesError("");
    try {
      const res = await courseAPI.getAll();
      const sorted = (res.data || []).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );
      setCourses(sorted);
      if (sorted.length > 0) {
        setSelectedCourseId((prev) => prev || sorted[0].id);
      }
    } catch (err) {
      setCoursesError("Failed to load courses.");
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    if (!selectedCourseId) return;
    let cancelled = false;
    courseAPI
      .getById(selectedCourseId)
      .then((res) => {
        if (!cancelled) setCourseKpis(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setCourseKpis(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCourseId]);

  const selectedCourse = courses.find((c) => c.id === selectedCourseId) || null;

  return (
    <div className="curriculum-page">
      <h3 className="page-subtitle">
        Manage district-wide Curriculum Docuements
      </h3>

      {/* Course selector bar */}
      <div className="curriculum-course-bar panel-card">
        <div className="curriculum-course-selector">
          <label
            className="curriculum-course-label"
            htmlFor="curriculum-course-select"
          >
            Course
          </label>
          <select
            id="curriculum-course-select"
            className="curriculum-course-select"
            value={selectedCourseId}
            onChange={(e) => setSelectedCourseId(e.target.value)}
            disabled={loadingCourses}
          >
            {loadingCourses && <option value="">Loading courses...</option>}
            {!loadingCourses && courses.length === 0 && (
              <option value="">No courses available</option>
            )}
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.grade_level ? ` (Grade ${c.grade_level})` : ""}
              </option>
            ))}
          </select>
        </div>

        {courseKpis && (
          <div className="curriculum-course-kpis">
            {courseKpis.unit_count != null && (
              <span className="curriculum-kpi">
                <strong>{courseKpis.unit_count}</strong> units
              </span>
            )}
            {courseKpis.lesson_count != null && (
              <span className="curriculum-kpi">
                <strong>{courseKpis.lesson_count}</strong> lessons
              </span>
            )}
            {courseKpis.teks_count != null && (
              <span className="curriculum-kpi">
                <strong>{courseKpis.teks_count}</strong> TEKS
              </span>
            )}
          </div>
        )}

        {selectedCourse && (
          <div className="curriculum-course-meta">
            {selectedCourse.course_category && (
              <span className="curriculum-meta-badge">
                {selectedCourse.course_category}
              </span>
            )}
          </div>
        )}
      </div>

      {coursesError && <p className="curriculum-error-msg">{coursesError}</p>}

      {/* Authoring order hint */}
      <div className="curriculum-order-hint">
        <span>Authoring order:</span>
        {TABS.map((tab, i) => (
          <span key={tab.id}>
            <button
              className={`curriculum-order-step${activeTab === tab.id ? " active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {i + 1}. {tab.label}
            </button>
            {i < TABS.length - 1 && (
              <span className="curriculum-order-arrow">→</span>
            )}
          </span>
        ))}
      </div>

      {/* Tab nav */}
      <nav className="curriculum-tab-nav">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`curriculum-tab-btn${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      <div className="curriculum-tab-content panel-card">
        {!selectedCourseId ? (
          <p className="curriculum-empty">
            Select a course above to begin editing.
          </p>
        ) : (
          <>
            {activeTab === "teks" && (
              <TeksTab
                courseId={selectedCourseId}
                onRefreshKpis={() => {
                  courseAPI
                    .getById(selectedCourseId)
                    .then((res) => setCourseKpis(res.data || null))
                    .catch(() => {});
                }}
              />
            )}
            {activeTab === "units-lessons" && (
              <UnitsLessonsTab
                courseId={selectedCourseId}
                onRefreshKpis={() => {
                  courseAPI
                    .getById(selectedCourseId)
                    .then((res) => setCourseKpis(res.data || null))
                    .catch(() => {});
                }}
              />
            )}
            {activeTab === "scope-sequence" && (
              <ScopeSequenceTab courseId={selectedCourseId} />
            )}
            {activeTab === "unit-guides" && (
              <UnitGuideTab courseId={selectedCourseId} />
            )}
            {activeTab === "pedagogy" && (
              <PedagogyTab courseId={selectedCourseId} />
            )}
            {activeTab === "assets" && (
              <AssetsTab courseId={selectedCourseId} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CurriculumPage;
